import { format, parseISO } from "date-fns";
import { ja } from "date-fns/locale";
import { useState } from "react";
import {
  PageContainer,
  PageContent,
  PageHeader,
} from "../components/layout/PageHeader";
import { Alert } from "../components/ui/Alert";
import { Breadcrumb } from "../components/ui/Breadcrumb";
import { Button } from "../components/ui/Button";
import { Card, CardContent, CardHeader } from "../components/ui/Card";
import { ConfirmModal } from "../components/ui/Modal";
import { Spinner } from "../components/ui/Spinner";
import {
  type Invite,
  useCreateInvite,
  useInvites,
  useRevokeInvite,
} from "../hooks/api/useInvites";

export function AdminInvitesPage() {
  const { data: invites, isLoading, error } = useInvites();
  const createInvite = useCreateInvite();
  const revokeInvite = useRevokeInvite();

  const [newInviteUrl, setNewInviteUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const handleCreateInvite = async (): Promise<boolean> => {
    setCreateError(null);
    setNewInviteUrl(null);
    setCopied(false);

    try {
      const invite = await createInvite.mutateAsync({});
      const url = `${window.location.origin}/invite/${invite.token}`;
      setNewInviteUrl(url);
      return true;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "招待リンクの発行に失敗しました";
      setCreateError(message);
      return false;
    }
  };

  const handleCopyUrl = async () => {
    if (!newInviteUrl) {
      return;
    }

    try {
      await navigator.clipboard.writeText(newInviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCreateError("クリップボードへのコピーに失敗しました");
    }
  };

  const handleRevokeClick = (token: string) => {
    setRevokeTarget(token);
    setRevokeError(null);
  };

  const handleRevokeConfirm = async () => {
    if (!revokeTarget) {
      return;
    }

    try {
      await revokeInvite.mutateAsync(revokeTarget);
      setRevokeTarget(null);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "招待の取り消しに失敗しました";
      setRevokeError(message);
    }
  };

  const handleRevokeClose = () => {
    setRevokeTarget(null);
    setRevokeError(null);
  };

  // ステータス別にフィルタリング
  const pendingInvites = invites?.filter((i) => i.status === "pending") ?? [];
  const usedInvites = invites?.filter((i) => i.status === "used") ?? [];
  const expiredOrRevokedInvites =
    invites?.filter((i) => i.status === "expired" || i.status === "revoked") ??
    [];

  return (
    <PageContainer>
      <PageHeader />
      <PageContent>
        <h1 className="sr-only">招待管理</h1>
        <Breadcrumb
          items={[
            { label: "管理", to: "/admin/invites" },
            { label: "招待管理" },
          ]}
        />
        <div className="space-y-6">
          {/* 招待リンク発行 */}
          <Card>
            <CardHeader title="新しい招待リンクを発行" />
            <CardContent>
              {createError && (
                <Alert
                  variant="error"
                  className="mb-4"
                  onClose={() => setCreateError(null)}
                >
                  {createError}
                </Alert>
              )}

              {newInviteUrl && (
                <Alert variant="success" className="mb-4">
                  <p className="mb-2 font-medium">
                    招待リンクを発行しました（有効期限: 7日間）
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={newInviteUrl}
                      className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                      data-testid="invite-url"
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleCopyUrl}
                    >
                      {copied ? "コピーしました" : "コピー"}
                    </Button>
                  </div>
                </Alert>
              )}

              <Button
                variant="primary"
                onClick={handleCreateInvite}
                isLoading={createInvite.isPending}
              >
                招待リンクを発行
              </Button>
            </CardContent>
          </Card>

          {/* 招待一覧 */}
          <Card>
            <CardHeader title="招待一覧" />
            <CardContent>
              {isLoading && (
                <div className="flex items-center justify-center py-8">
                  <Spinner size="lg" />
                </div>
              )}

              {error && (
                <Alert variant="error">
                  招待一覧の取得に失敗しました。再読み込みしてください。
                </Alert>
              )}

              {!(isLoading || error) && (
                <div className="space-y-6">
                  {/* 有効な招待 */}
                  <InviteSection
                    title="有効な招待"
                    invites={pendingInvites}
                    emptyMessage="有効な招待はありません"
                    onRevoke={handleRevokeClick}
                    showRevokeButton
                    emptyIcon={
                      <EmptyStateIcon d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                    }
                    emptyAction={
                      <Button
                        variant="primary"
                        size="md"
                        isLoading={createInvite.isPending}
                        aria-busy={createInvite.isPending}
                        data-testid="empty-state-invite-cta"
                        onClick={async () => {
                          const success = await handleCreateInvite();
                          if (success) {
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }
                        }}
                      >
                        招待リンクを発行
                      </Button>
                    }
                  />

                  {/* 使用済み招待 */}
                  <InviteSection
                    title="使用済み"
                    invites={usedInvites}
                    emptyMessage="使用済みの招待はありません"
                    emptyIcon={
                      <EmptyStateIcon d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                    }
                  />

                  {/* 期限切れ・取り消し済み */}
                  <InviteSection
                    title="期限切れ・取り消し済み"
                    invites={expiredOrRevokedInvites}
                    emptyMessage="期限切れ・取り消し済みの招待はありません"
                    emptyIcon={
                      <EmptyStateIcon d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                    }
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </PageContent>

      <ConfirmModal
        isOpen={!!revokeTarget}
        onClose={handleRevokeClose}
        onConfirm={handleRevokeConfirm}
        title="招待の取り消し"
        message="この招待リンクを取り消しますか？取り消すと、このリンクは使用できなくなります。"
        confirmText="取り消す"
        variant="danger"
        isLoading={revokeInvite.isPending}
      >
        {revokeError && (
          <Alert variant="error" className="mt-3">
            {revokeError}
          </Alert>
        )}
      </ConfirmModal>
    </PageContainer>
  );
}

function EmptyStateIcon({ d }: { d: string }) {
  return (
    <svg
      className="h-12 w-12 text-gray-300"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

interface InviteSectionProps {
  title: string;
  invites: Invite[];
  emptyMessage: string;
  emptyIcon?: React.ReactNode;
  emptyAction?: React.ReactNode;
  onRevoke?: (token: string) => void;
  showRevokeButton?: boolean;
}

function InviteSection({
  title,
  invites,
  emptyMessage,
  emptyIcon,
  emptyAction,
  onRevoke,
  showRevokeButton = false,
}: InviteSectionProps) {
  if (invites.length === 0) {
    return (
      <div>
        <h3 className="mb-4 font-medium text-gray-900">{title}</h3>
        <div className="flex flex-col items-center gap-4 py-12 text-gray-500">
          {emptyIcon}
          <p>{emptyMessage}</p>
          {emptyAction}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="mb-3 font-medium text-gray-900">
        {title}（{invites.length}件）
      </h3>

      {/* モバイル: カード表示 */}
      <div className="space-y-3 md:hidden">
        {invites.map((invite) => (
          <InviteCard
            key={invite.token}
            invite={invite}
            onRevoke={onRevoke}
            showRevokeButton={showRevokeButton}
          />
        ))}
      </div>

      {/* デスクトップ: テーブル表示 */}
      <div className="hidden md:block overflow-hidden rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                トークン
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                ステータス
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                発行日時
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                有効期限
              </th>
              {showRevokeButton && (
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  操作
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {invites.map((invite) => (
              <InviteRow
                key={invite.token}
                invite={invite}
                onRevoke={onRevoke}
                showRevokeButton={showRevokeButton}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface InviteCardProps {
  invite: Invite;
  onRevoke?: (token: string) => void;
  showRevokeButton?: boolean;
}

function InviteCard({
  invite,
  onRevoke,
  showRevokeButton = false,
}: InviteCardProps) {
  const statusLabels: Record<Invite["status"], string> = {
    pending: "有効",
    used: "使用済み",
    expired: "期限切れ",
    revoked: "取り消し済み",
  };

  const statusColors: Record<Invite["status"], string> = {
    pending: "bg-green-100 text-green-700",
    used: "bg-blue-100 text-blue-700",
    expired: "bg-gray-100 text-gray-700",
    revoked: "bg-red-100 text-red-700",
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-2">
      <div className="flex items-center justify-between">
        <span className="font-mono text-sm text-gray-900">
          {invite.token.slice(0, 8)}...
        </span>
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusColors[invite.status]}`}
        >
          {statusLabels[invite.status]}
        </span>
      </div>
      {invite.status === "used" && invite.usedBy && (
        <p className="text-sm text-gray-500">使用者: {invite.usedBy}</p>
      )}
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-gray-400">発行</span>
          <p className="text-gray-600">
            {format(parseISO(invite.createdAt), "MM/dd HH:mm", { locale: ja })}
          </p>
        </div>
        <div>
          <span className="text-gray-400">期限</span>
          <p className="text-gray-600">
            {format(parseISO(invite.expiresAt), "MM/dd HH:mm", { locale: ja })}
          </p>
        </div>
      </div>
      {showRevokeButton && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onRevoke?.(invite.token)}
          className="w-full"
        >
          取り消し
        </Button>
      )}
    </div>
  );
}

interface InviteRowProps {
  invite: Invite;
  onRevoke?: (token: string) => void;
  showRevokeButton?: boolean;
}

function InviteRow({
  invite,
  onRevoke,
  showRevokeButton = false,
}: InviteRowProps) {
  const statusLabels: Record<Invite["status"], string> = {
    pending: "有効",
    used: "使用済み",
    expired: "期限切れ",
    revoked: "取り消し済み",
  };

  const statusColors: Record<Invite["status"], string> = {
    pending: "bg-green-100 text-green-700",
    used: "bg-blue-100 text-blue-700",
    expired: "bg-gray-100 text-gray-700",
    revoked: "bg-red-100 text-red-700",
  };

  return (
    <tr>
      <td className="whitespace-nowrap px-4 py-3 text-sm font-mono text-gray-900">
        {invite.token.slice(0, 8)}...
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm">
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusColors[invite.status]}`}
        >
          {statusLabels[invite.status]}
        </span>
        {invite.status === "used" && invite.usedBy && (
          <span className="ml-2 text-gray-500">({invite.usedBy})</span>
        )}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
        {format(parseISO(invite.createdAt), "yyyy/MM/dd HH:mm", { locale: ja })}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
        {format(parseISO(invite.expiresAt), "yyyy/MM/dd HH:mm", { locale: ja })}
      </td>
      {showRevokeButton && (
        <td className="whitespace-nowrap px-4 py-3 text-sm">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onRevoke?.(invite.token)}
          >
            取り消し
          </Button>
        </td>
      )}
    </tr>
  );
}
